import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Store, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, eur, fileUrl, CATEGORIES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page, Empty } from "@/components/Page";

const sel = "h-11 rounded-full border border-white/10 bg-[#121215] px-4 text-sm outline-none";

const ListingCard = ({ l, children }) => (
  <div className="card-lux group overflow-hidden" data-testid={`listing-card-${l.id}`}>
    <Link to={`/marktplatz/${l.id}`} className="block">
      <div className="relative aspect-[4/5] overflow-hidden bg-[#121215]">
        <img src={fileUrl(l.image_paths[0])} alt={l.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        {l.status === "sold" && <span className="absolute inset-0 flex items-center justify-center bg-black/60 font-serif text-2xl">Verkauft</span>}
      </div>
      <div className="p-4">
        <p className="text-[10px] uppercase tracking-widest text-neutral-500">{l.brand || "Ohne Marke"} · {l.size || "–"} · {l.condition}</p>
        <p className="mt-1 truncate font-serif text-lg">{l.title}</p>
        <p className="mt-1 text-sm font-semibold">{eur(l.price)}</p>
      </div>
    </Link>
    {children}
  </div>
);

function Mine() {
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState({ bought: [], sold: [] });
  const load = () => {
    api.get("/listings/mine").then(({ data }) => setItems(data));
    api.get("/orders/mine").then(({ data }) => setOrders(data));
  };
  useEffect(load, []);
  const remove = async (id) => { try { await api.delete(`/listings/${id}`); toast.success("Anzeige entfernt"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const earned = orders.sold.reduce((s, o) => s + o.seller_payout, 0);
  return (
    <div className="space-y-10" data-testid="my-listings">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        {[["Aktive Anzeigen", items.filter((i) => i.status === "active").length], ["Verkauft", orders.sold.length], ["Dein Erlös", eur(earned)]].map(([l, v]) => (
          <div key={l} className="surface p-6"><p className="label-over">{l}</p><p className="mt-3 font-serif text-3xl" data-testid={`my-stat-${l.replace(/\s+/g, "-").toLowerCase()}`}>{v}</p></div>
        ))}
      </div>
      {items.length === 0 ? <Empty icon={Store} text="Du hast noch nichts zum Verkauf angeboten." testid="my-listings-empty"><Link to="/marktplatz/verkaufen" className="btn-ghost">Artikel verkaufen</Link></Empty> : (
        <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4">
          {items.map((l) => (
            <ListingCard key={l.id} l={l}>
              <div className="flex items-center justify-between border-t border-white/[0.06] px-4 py-3 text-xs text-neutral-400">
                <span>Auszahlung {eur(l.seller_payout)}</span>
                {l.status === "active" && <button onClick={() => remove(l.id)} className="hover:text-white" data-testid={`remove-listing-${l.id}`}><Trash2 className="h-4 w-4" /></button>}
              </div>
            </ListingCard>
          ))}
        </div>
      )}
      {orders.bought.length > 0 && (
        <div><p className="label-over">Deine Käufe</p>
          <ul className="mt-4 space-y-2" data-testid="my-purchases">{orders.bought.map((o) => <li key={o.id} className="surface flex justify-between p-4 text-sm"><span>{o.title}</span><span>{eur(o.amount)}</span></li>)}</ul>
        </div>
      )}
    </div>
  );
}

export default function Marketplace() {
  const { user } = useAuth();
  const [tab, setTab] = useState("browse");
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ q: "", category: "" });

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    const t = setTimeout(() => api.get("/listings", { params }).then(({ data }) => setItems(data)), 250);
    return () => clearTimeout(t);
  }, [f]);

  return (
    <Page over="Marktplatz" title="Second Hand mit Stil" sub="Kaufe und verkaufe Lieblingsteile aus der Community. Sicher bezahlt per PayPal."
      right={<Link to="/marktplatz/verkaufen" className="btn-cream" data-testid="market-sell-button"><Plus className="h-4 w-4" /> Artikel verkaufen</Link>} testid="marketplace-page">
      {user && (
        <div className="mb-8 flex w-fit rounded-full border border-white/10 p-1">
          {[["browse", "Entdecken"], ["mine", "Meine Anzeigen"]].map(([t, l]) => (
            <button key={t} onClick={() => setTab(t)} className={`rounded-full px-5 py-2 text-sm ${tab === t ? "bg-[#FAF7F0] font-semibold text-black" : "text-neutral-400"}`} data-testid={`market-tab-${t}`}>{l}</button>
          ))}
        </div>
      )}
      {tab === "mine" ? <Mine /> : (
        <>
          <div className="mb-8 flex flex-wrap gap-3">
            <div className="relative min-w-[220px] flex-1">
              <Search className="absolute left-4 top-3.5 h-4 w-4 text-neutral-500" />
              <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Suche im Marktplatz" className={`${sel} w-full pl-11`} data-testid="market-search-input" />
            </div>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={sel} data-testid="market-category-filter">
              <option value="">Alle Kategorien</option>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          {items.length === 0 ? <Empty icon={Store} text="Noch keine Artikel im Marktplatz. Sei die erste Person, die etwas verkauft!" testid="market-empty" /> : (
            <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4" data-testid="market-grid">
              {items.map((l) => <ListingCard key={l.id} l={l} />)}
            </div>
          )}
        </>
      )}
    </Page>
  );
}
